/**
 * catalog/names — THE id → human NAME resolver for everything an export WRITES.
 *
 * `ProductTemplate.phoneModelId` / `caseStyleId` are catalog IDS (cloud `phone_models` /
 * `case_styles` rows, `cloud/app/catalog`). Four surfaces used to print them raw, because
 * `selectionFromTemplate` seeded its display context from the ids themselves:
 *
 *   - the export FOLDER tree  → `Mockups/TPU Phone Case/040d67ee/040d67ee 2b53 42b1 8ec3 B5fc1f6a2580/…`
 *                               (the id's own 8-char prefix as the Brand folder, and the id
 *                               with its dashes turned to spaces and Title Cased as the Model)
 *   - the marketplace SHEET   → `Phone Model` / `Case Style` columns carrying bare UUIDs
 *   - the SKU                 → `040D67EE-2B53-…-2CB95021-…-LEOPARD-AUDIT`
 *   - the Batch template CHIP → two bare UUIDs as its subtitle
 *
 * `templateLabels` already refuses to print an id on the template INDEX surfaces. This module
 * is the same refusal for the WRITTEN ones, and it is the only place the rule lives: a name is
 * resolved from the catalog lists the app already loads for the wizard, then from the
 * server-resolved `display` fields, then from the template's own name, and only then from a
 * SHORT id — never a raw identifier, and never one dressed up as a title.
 */
import type { CaseStyleOption, PhoneModelOption, ProductTemplate } from "@genz/platform";
import { modelLabel } from "../templates/templateLabels.js";

/** The "no value" glyph `templateLabels` prints. A folder must never be called this. */
const EM_DASH = "—";

/** A canonical UUID — what the cloud actually stores in `phone_model_id` / `case_style_id`. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The shortest hex run that cannot be a word.
 *
 * review-2 item 5: the threshold was 12, and English writes plenty of words out of `a`-`f` —
 * "Facade Decade" is exactly twelve, so a case style by that name was called an identifier
 * and replaced with "Style 040d67ee" in the sheet and on disk. 20 sits above every all-hex
 * English phrase we could construct and below a UUID's 32 characters, so the disguise this
 * rule exists to catch (a UUID with its dashes turned to spaces) is still caught whole.
 */
const MIN_OPAQUE_HEX = 20;

/**
 * A bare identifier no human should be shown. Two shapes only, both of which a product name
 * cannot accidentally take: a canonical dashed UUID, or an unbroken hex run of at least
 * {@link MIN_OPAQUE_HEX} characters (a dash-stripped UUID, a digest, a ULID-length token).
 */
export function isOpaqueId(value: string | null | undefined): boolean {
  const raw = (value ?? "").trim();
  if (!raw) return false;
  if (UUID_RE.test(raw)) return true;
  const compact = raw.replace(/[\s-]/g, "");
  return compact.length >= MIN_OPAQUE_HEX && /^[0-9a-f]+$/i.test(compact);
}

/** The head of an id, for the one case where nothing else is known: "040d67ee". */
export function shortId(id: string | null | undefined): string {
  return (id ?? "").replace(/[^0-9a-z]/gi, "").slice(0, 8);
}

/**
 * Print a NAME, never a raw id. An opaque identifier becomes `<noun> <shortId>` — "Model
 * 040d67ee" — which an operator can still quote to support, and which cannot be mistaken for
 * a product. Blank falls through to `fallback` (and then to the same short-id form).
 */
export function readableName(
  value: string | null | undefined,
  opts: { noun: string; fallback?: string; id?: string },
): string {
  const raw = (value ?? "").trim();
  if (raw && raw !== EM_DASH && !isOpaqueId(raw)) return raw;
  const fallback = (opts.fallback ?? "").trim();
  if (fallback && fallback !== EM_DASH && !isOpaqueId(fallback)) return fallback;
  const head = shortId(opts.id ?? raw);
  return head ? `${opts.noun} ${head}` : opts.noun;
}

/**
 * Slug rule for a resolved NAME — UNICODE-aware.
 *
 * Deliberately not `refs.slugify`, which also eats a trailing `.token` ("iPhone 15 Pro.5"
 * must keep its tail). And deliberately not the plain `[a-z0-9]` rule `stores/selection`
 * used to carry (review-2 item 4): that dropped every non-Latin script, so "小米 14" and
 * "小米 15" both slugged to the empty string, fell back to the literal `"phone"`, and shared
 * one SKU and one output path — the silent-overwrite class this whole scheme exists to
 * prevent. Any letter or digit in any script is kept.
 *
 * Combining accents are folded onto their Latin base first (NFKD, then strip the marks), so
 * "Café Pro" and "Cafe Pro" cannot slug to two different SKUs for one product.
 */
export function catalogSlug(name: string): string {
  return (name ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
}

/** The catalog lists the wizard/editor already fetch (`listPhoneModels` / `listCaseStyles`). */
export interface CatalogNameLists {
  phoneModels?: readonly PhoneModelOption[];
  caseStyles?: readonly CaseStyleOption[];
}

/** The human names + the slugs derived FROM them (never from an id). */
export interface ProductNames {
  /** `<Brand>` folder + the Brand column. `""` when genuinely unknown — the folder segment is
   *  then SKIPPED rather than guessed, exactly as `templateLabels.brandOf` refuses to guess. */
  brand: string;
  /** `<Model>` folder + the sheet's `Phone Model` column. Never blank, never an id. */
  model: string;
  /** The sheet's `Case Style` column. Never blank, never an id. */
  caseStyle: string;
  /**
   * SKU/path slug for the device (spec 005: SKU = `{phoneSlug}-{caseStyleSlug}-{designSlug}`).
   *
   * The CATALOG ROW's own `slug` when it has one, and only otherwise a slug of the label.
   * That column is immutable server-side — `PATCH /api/phone-models` refuses to change it
   * because it keys stored asset paths and every `url_rules.path_template` — and the Mock-up
   * funnel has always passed it verbatim. Deriving it from the label gave one device two
   * SKUs depending on which door the operator came through, and made an admin's in-place
   * model rename re-slug a SKU that was already published to a marketplace.
   */
  phoneSlug: string;
  /**
   * SKU/path slug for the case style — always derived from the label.
   *
   * `case_styles` has no slug column (`CaseStyleOption` is `{id, code, label}`), and both
   * entry points reach it through the same resolved label, so there is no second source to
   * disagree with. Its `code` is NOT used: it is a short internal token ("CLR"), and swapping
   * it in would rewrite every SKU's middle field for no correctness gain.
   */
  caseStyleSlug: string;
}

/** First candidate that is a real name (non-blank, not an em dash, not an id). */
function firstName(candidates: (string | null | undefined)[]): string {
  for (const c of candidates) {
    const raw = (c ?? "").trim();
    if (raw && raw !== EM_DASH && !isOpaqueId(raw)) return raw;
  }
  return "";
}

/**
 * Resolve a template's catalog ids into the names every written output prints.
 *
 * Order, per field, and it is the same order everywhere:
 *   1. the CATALOG list row for that id (`listPhoneModels` / `listCaseStyles`) — the name the
 *      operator picked in the wizard;
 *   2. the server-resolved `display` fields on the template row itself;
 *   3. the template's own display NAME — a name the operator wrote, so it is always readable;
 *   4. `<noun> <shortId>` — readable, quotable, and unmistakably not a product.
 *
 * `lists` is optional on purpose: a surface that has not fetched the catalog (or whose fetch
 * failed — `loadCatalogList` degrades to `[]`) still gets names from steps 2-4 instead of ids.
 */
export function resolveProductNames(t: ProductTemplate, lists: CatalogNameLists = {}): ProductNames {
  const phone = t.phoneModelId
    ? lists.phoneModels?.find((p) => p.id === t.phoneModelId)
    : undefined;
  const style = t.caseStyleId
    ? lists.caseStyles?.find((c) => c.id === t.caseStyleId)
    : undefined;

  // Brand is never GUESSED from the model label ("OnePlus 6" vs "One Plus" is a coin flip, and
  // splitting an id is what produced the `040d67ee/` folder). Catalog, then server, then none.
  const brand = firstName([phone?.brand, t.brand, t.display?.brand]);

  const model = readableName(
    firstName([
      phone ? modelLabel({ brand: phone.brand, model: phone.model }) : "",
      t.display?.model ? modelLabel({ brand, model: t.display.model }) : "",
    ]),
    { noun: "Model", fallback: t.name, id: t.phoneModelId },
  );

  const caseStyle = readableName(
    firstName([style?.label, t.display?.style]),
    { noun: "Style", fallback: t.name, id: t.caseStyleId },
  );

  return {
    brand,
    model,
    caseStyle,
    phoneSlug: phone?.slug?.trim() || catalogSlug(model) || "phone",
    caseStyleSlug: catalogSlug(caseStyle) || "case",
  };
}
